import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { IntegrationServiceId } from '../../../common/integrations'
import { usePostConnectSteps, type PostConnectIntent, type PostConnectStep } from './usePostConnectSteps'
import { PostConnectModalShell } from './PostConnectModalShell'
import { CalendarEventFiltersStep } from './CalendarScreens/CalendarEventFiltersStep'
import { CalendarSuccessStep } from './CalendarScreens/CalendarSuccessStep'
import { CrmCalendarSyncStep } from './CrmScreens/CrmCalendarSyncStep'
import { ContactSyncStep } from './CrmScreens/ContactSyncStep'
import { ContactsSuccessStep } from './CrmScreens/ContactsSuccessStep'
import { ZoomSettingsStep } from './ZoomScreens/ZoomSettingsStep'
import { ZoomSuccessStep } from './ZoomScreens/ZoomSuccessStep'
import { ROUTER_BASEPATH } from '../../../common/constants'
import { validateAndGetInternalRedirectUrl } from '../../../common/internalRedirect'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking/hooks'
import { GrowthTrackEvent } from '../../../apps/professional-app/fa/common/tracking'
import { deriveTrackingOriginFromReturnPath } from '../tracking_utils'

export const PostConnectPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>()
  const [searchParams] = useSearchParams()

  const returnTo = validateAndGetInternalRedirectUrl(searchParams.get('returnTo')) ?? `${ROUTER_BASEPATH}/workspace/upcoming`
  const intent = (searchParams.get('intent') ?? null) as PostConnectIntent | null
  const validServiceId = serviceId as IntegrationServiceId

  const steps = usePostConnectSteps(validServiceId, intent)
  const [stepIndex, setStepIndex] = useState(0)

  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const origin = useMemo(() => deriveTrackingOriginFromReturnPath(returnTo), [returnTo])

  const handleNavigateToSettings = useCallback(() => {
    navigate(`/account/integration-settings/integrations/${validServiceId}/settings`)
  }, [navigate, validServiceId])

  const handleExitToEventFilters = useCallback(() => {
    trackingClient.track({
      eventName: GrowthTrackEvent.CALENDAR_POST_CONNECT_EVENT_FILTERS_CLICKED,
      eventDetails: { integrationId: validServiceId, ...(origin !== undefined && { origin }) },
    })
    navigate('/account/profile-settings/meeting-assistant')
  }, [navigate, trackingClient, validServiceId, origin])

  const handleDone = useCallback(() => {
    window.location.href = returnTo
  }, [returnTo])

  const handleNext = useCallback(() => {
    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1)
    } else {
      handleDone()
    }
  }, [stepIndex, steps.length, handleDone])

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1))
  }, [])

  const handleCalendarDone = useCallback(() => {
    trackingClient.track({
      eventName: GrowthTrackEvent.CALENDAR_POST_CONNECT_DONE_CLICKED,
      eventDetails: { integrationId: validServiceId, ...(origin !== undefined && { origin }) },
    })
    handleDone()
  }, [trackingClient, validServiceId, origin, handleDone])

  const handleContactsDone = useCallback(() => {
    trackingClient.track({
      eventName: GrowthTrackEvent.CONTACTS_POST_CONNECT_DONE_CLICKED,
      eventDetails: { integrationId: validServiceId, ...(origin !== undefined && { origin }) },
    })
    handleDone()
  }, [trackingClient, validServiceId, origin, handleDone])

  const handleClose = useCallback(() => {
    trackingClient.track({
      eventName: GrowthTrackEvent.POST_CONNECT_CLOSE_CLICKED,
      eventDetails: { integrationId: validServiceId, ...(origin !== undefined && { origin }) },
    })
    handleDone()
  }, [trackingClient, validServiceId, origin, handleDone])

  useEffect(() => {
    if (steps.length === 0) {
      handleDone()
    }
  }, [steps.length, handleDone])

  if (steps.length === 0) {
    return null
  }

  const currentStep: PostConnectStep = steps[stepIndex]
  const backHandler = stepIndex === 0 ? undefined : handleBack
  const isDone = stepIndex === steps.length - 1
  const useConnectedHeader = stepIndex === 0

  const stepContent = (() => {
    switch (currentStep) {
      case 'calendar-event-filters':
        return (
          <CalendarEventFiltersStep
            serviceId={validServiceId}
            useConnectedHeader={useConnectedHeader}
            onContinue={handleNext}
            onExitToEventFilters={handleExitToEventFilters}
            onBack={backHandler}
            isDone={isDone}
          />
        )
      case 'crm-calendar-sync':
        return (
          <CrmCalendarSyncStep
            serviceId={validServiceId}
            useConnectedHeader={useConnectedHeader}
            onContinue={handleNext}
            onExit={handleDone}
            onBack={backHandler}
          />
        )
      case 'contact-sync':
        return (
          <ContactSyncStep
            serviceId={validServiceId}
            useConnectedHeader={useConnectedHeader}
            onContinue={handleNext}
            onExit={handleDone}
            onNavigateToSettings={handleNavigateToSettings}
            onBack={backHandler}
          />
        )
      case 'calendar-success':
        return <CalendarSuccessStep serviceId={validServiceId} onContinue={handleCalendarDone} />
      case 'contacts-success':
        return <ContactsSuccessStep serviceId={validServiceId} onContinue={handleContactsDone} />
      case 'zoom-settings':
        return <ZoomSettingsStep onContinue={handleNext} onBack={backHandler} />
      case 'zoom-success':
        return <ZoomSuccessStep onContinue={handleDone} />
      default:
        return null
    }
  })()

  return <PostConnectModalShell onClose={handleClose}>{stepContent}</PostConnectModalShell>
}
